import { type CSSProperties, useEffect, useRef } from "react";
import { Icon } from "@/presentation/components/icons/Icon";
import type { MonName } from "@/presentation/components/icons/icon-names";
import {
  LABELS,
  LEVEL_KANJI_NAMES,
  LEVEL_RANK_NAMES,
  parseLevelNumber,
} from "@/presentation/i18n/labels";
import { computeStampTilt } from "@/presentation/screens/StampBook/stamp-tilt";
import "./StampAward.css";

/** 押印演出の総尺（唯一の300ms超の例外・ui-design.md §7.5）。 */
const SEAL_DURATION_MS = 480;
/** 演出開始から稽古後の話（V-5）へ自動で進むまでの尺（モック `asset-prompts` 系ドキュメントの表記どおり）。 */
const AUTO_ADVANCE_DELAY_MS = 1400;

interface StampAwardProps {
  readonly levelId: string;
  /**
   * 演出完了（自動 or `⏎`早送り）時に一度だけ呼ぶ。
   * **朱印の永続化はこのコンポーネントの責務ではない**。呼び出し側（`useLevelFlow.completeExam`）が
   * このビューへ切り替える前に保存を完了させている（途中で閉じても失われないという要件を、
   * 「演出は保存結果を表示するだけ」という設計で満たす）。
   */
  readonly onComplete: () => void;
}

/**
 * 朱印を授かる演出（V-4。ui-design.md §7.5）。Z1（帳場）は隠す（screen-flow.md §7.2）。
 * 打ち音「ドン」（key.mp3等と同じ音声制御の枠組み）は計画13で音声制御全体を実装する際に追加する
 * （このコンポーネント単体では鳴らさない。既定ミュート・フェッチ制御を伴う音声基盤が未実装のため）。
 */
export function StampAward({ levelId, onComplete }: StampAwardProps) {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const firedRef = useRef(false);

  useEffect(() => {
    firedRef.current = false;
    const timer = window.setTimeout(() => {
      if (!firedRef.current) {
        firedRef.current = true;
        onCompleteRef.current();
      }
    }, AUTO_ADVANCE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  function handleFastForward() {
    if (firedRef.current) {
      return;
    }
    firedRef.current = true;
    onComplete();
  }

  const number = parseLevelNumber(levelId);
  const rank = number !== null ? LEVEL_RANK_NAMES[number] : undefined;
  const kanji = number !== null ? LEVEL_KANJI_NAMES[number] : undefined;
  const iconName: MonName = number !== null ? (`mon-${number}` as MonName) : "mon-master";
  const tiltDegrees = computeStampTilt(levelId);

  return (
    <div className="stamp-award" style={{ animationDuration: `${SEAL_DURATION_MS}ms` }}>
      <div className="stamp-award__sheet">
        <span
          className="stamp-award__mon"
          style={{ "--stamp-award-tilt": `${tiltDegrees}deg` } as CSSProperties}
        >
          <Icon name={iconName} decorative size={160} />
        </span>
        <p className="stamp-award__title">{LABELS.stamp}を授かった</p>
        {rank && kanji && (
          <p className="stamp-award__name">
            <span className="stamp-award__rank">{rank}</span> {kanji}
          </p>
        )}
      </div>
      <button type="button" className="stamp-award__skip" onClick={handleFastForward}>
        進む <span className="stamp-award__skip-key">⏎</span>
      </button>
    </div>
  );
}
